import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Tooltip } from "../../components/Shared/Tooltip";
import { useMediaQuery } from 'react-responsive';

interface Props {
    chartColors?: {
        colors: Array<{colorCode: string, statusText: string}>;
    };
    chartData: {
        name: string;
        value: number;
    }[];
    displayCount?: boolean;
    isShowStatus?: boolean;
    style: any;
    hiddenStatuses?: string[];
    onChartClick?: () => void;
}
export const PieChartComponet: React.FC<Props> = (props) => {
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });    
    const [clickedValue, setClickedValue] = useState<number | null>(null);
    const [clickedName, setClickedName] = useState<string>('');
    const [clickedColor, setClickedColor] = useState<string>('');
    const [mouseHover, setMouseHover] = useState<boolean>(false);
    const [hoveredItem, setHoveredItem] = useState<{name: string; value: number} | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const style = props.style;

    const filteredChartData = props.chartData.filter(
        item => !props.hiddenStatuses?.includes(item.name)
    );

    const filteredColors = props.chartColors!.colors.filter(
        color => !props.hiddenStatuses?.includes(color.statusText)
    );
    
    const filteredColorsArray = filteredColors.map(item => item.colorCode);

    const handlePieChartClick = (data: any, index: number) => {
        if (data && props.chartData[index]) {
            setClickedValue(filteredChartData[index].value);
            setClickedName(filteredChartData[index].name);
            const clickedItem = filteredChartData[index];
            const colorObj = props.chartColors!.colors.find(
                colorItem => colorItem.statusText === clickedItem.name
            );
            if (colorObj) {
                setClickedColor(colorObj.colorCode);
            }
            if (props.onChartClick) {
                props.onChartClick();
            }
        }
    };

    const handleMouseEnter = (data: any, index: number, e: React.MouseEvent) => {
        setMouseHover(true);
        if (data && filteredChartData[index]) {
            setHoveredItem(filteredChartData[index]);
        }
    };

    const handleMouseLeave = () => {
        setMouseHover(false);
        setHoveredItem(null);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({
            x: isMobile ? e.clientX - rect.left - 20 : e.clientX - rect.left - 60,
            y: isMobile ? e.clientY - rect.top + 100 : e.clientY - rect.top - 10
        });
    };

    return (
        <div style={style.chartWrapper} onMouseMove={handleMouseMove}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data= {filteredChartData}
                        innerRadius= "70%"
                        outerRadius= "100%"
                        dataKey= "value"
                        startAngle= {90} 
                        endAngle= {-270} 
                        onClick={handlePieChartClick}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {filteredChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={filteredColorsArray[index % filteredColorsArray.length]} style={{ outline: "none"}}/>
                        ))}
                    </Pie>                    
                </PieChart>
            </ResponsiveContainer>
            {(mouseHover && hoveredItem) && (
                <Tooltip styles={{...(style.toolTipWrapper), left: tooltipPosition.x, top: tooltipPosition.y}}>
                    <div >
                        {(() => {
                            const colorObj = props.chartColors!.colors.find(
                                colorItem => colorItem.statusText === hoveredItem!.name
                            );
                            return (
                                <div style={style.toolTip}>
                                    <div style={{...style.toolTip.colorCode, backgroundColor: colorObj?.colorCode}}/>
                                    {props.isShowStatus ? 
                                        <span style={style.toolTip.status}>
                                            {hoveredItem!.name} : {hoveredItem!.value}
                                        </span> :
                                        <span style={style.toolTip.status}>
                                            {hoveredItem!.name} : {hoveredItem!.value.toFixed(2)}%
                                        </span>
                                    }
                                </div>
                            );
                        })()}
                    </div>
                </Tooltip>
            )}
            {props.displayCount && <div style={style.countWrapper}>
                {clickedValue !== null && (
                    <>
                        <div style={{...style.count, color: clickedColor}}>
                            {clickedValue}
                        </div>
                        <div style={{...style.name, color: clickedColor}}>
                            {clickedName}
                        </div>
                    </>
                )}
            </div>}
        </div>
    );
}